/**
 * ensureWebFontsLoaded — idempotent client-side web-font injector
 *
 * The single loading primitive for every surface that renders V2 screens
 * in a live document: the published runtime, all editor previews, theme
 * previews, and (via targetDocument) WebEmbed iframes.
 *
 * SSR-safe per v2-web-shell rule 15a: bails before touching any browser
 * API when no document exists.
 *
 * @module font-config/utils
 */

import type { WebFontAsset } from '../types/fontConfig.types';
import { buildWebFontFaceCss, getPreloadFace } from './webFontCss';

const FONTS_ORIGIN = 'https://storage.googleapis.com';
const STYLE_MARKER = 'data-segmently-web-fonts';

/**
 * Families already injected, tracked per Document so the runtime bake,
 * provider effects, and editor previews never duplicate @font-face rules
 * no matter how many entry points fire.
 */
const loadedFamilies = new WeakMap<Document, Set<string>>();

/**
 * Inject preconnect + preload + @font-face (real faces and the
 * metric-adjusted fallback face) for the given assets into the target
 * document's <head>. Repeat calls with known families are no-ops.
 */
export function ensureWebFontsLoaded(
  assets: WebFontAsset[] | null | undefined,
  targetDocument?: Document,
): void {
  if (typeof document === 'undefined' && !targetDocument) return; // SSR guard — first statement
  const doc = targetDocument ?? document;
  if (!assets?.length) return;

  const head = doc.head ?? doc.getElementsByTagName('head')[0];
  if (!head) return;

  let loaded = loadedFamilies.get(doc);
  if (!loaded) {
    loaded = new Set<string>();
    loadedFamilies.set(doc, loaded);
    // The published-HTML bake may have injected families before hydration —
    // respect its marker so the runtime effect doesn't re-declare them.
    head
      .querySelectorAll(`style[${STYLE_MARKER}]`)
      .forEach((el) => {
        (el.getAttribute(STYLE_MARKER) ?? '')
          .split(' ')
          .filter(Boolean)
          .forEach((family) => loaded!.add(family));
      });
  }

  const fresh = assets.filter((asset) => !loaded!.has(asset.family));
  if (!fresh.length) return;
  fresh.forEach((asset) => loaded!.add(asset.family));

  if (!head.querySelector(`link[rel="preconnect"][href="${FONTS_ORIGIN}"]`)) {
    const link = doc.createElement('link');
    link.rel = 'preconnect';
    link.href = FONTS_ORIGIN;
    link.crossOrigin = 'anonymous';
    head.appendChild(link);
  }

  for (const asset of fresh) {
    const face = getPreloadFace(asset);
    if (!face) continue;
    if (head.querySelector(`link[rel="preload"][href="${face.woff2}"]`)) continue;
    const link = doc.createElement('link');
    link.rel = 'preload';
    link.setAttribute('as', 'font');
    link.type = 'font/woff2';
    link.href = face.woff2;
    link.crossOrigin = 'anonymous';
    if (face.sri) link.integrity = face.sri;
    head.appendChild(link);
  }

  const style = doc.createElement('style');
  style.setAttribute(STYLE_MARKER, fresh.map((asset) => asset.family).join(' '));
  style.textContent = buildWebFontFaceCss(fresh);
  head.appendChild(style);
}
